import { useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { MenuBadge } from "@impero-com/ui";
import { useNavigate } from "react-router-dom";

import { OrgApplicationBar } from "components/applicationBar/orgApplicationBar";
import { LegacyRoute } from "legacyRoutes";
import { WebRoute } from "routes";
import { navigateTo } from "utils/legacyNavigation";
import { useUserStatusQuery } from "utils/queries/useUserStatusQuery";

import { useFeatureFlag } from "../../utils/hooks/useFeatureFlag";
import { useNewMenu } from "../navigation/useNewMenu";

import { DashboardPendingActivitiesList } from "./DashboardPendingActivitiesList";
import { PendingActivityTableRow } from "./types";
import { dashboardPendingActivitiesUrl } from "./urlUtils";
import { usePendingActivitiesCount } from "./usePendingActivitiesCount";

export function DashboardPendingActivitiesContainer({
    rows,
    arrivedFromPendingTests,
}: {
    rows: PendingActivityTableRow[];
    arrivedFromPendingTests: boolean;
}) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { data: userStatus } = useUserStatusQuery();

    const { count, isOverdue } = usePendingActivitiesCount(rows);

    const controlHealthDashboard = useFeatureFlag("controlHealthDashboard");
    const newMenu = useNewMenu();

    const baseTabs = [
        {
            id: "tab-dashboard",
            label: t("Dashboard_ControlPrograms_Title"),
            onClick: () => navigateTo(LegacyRoute.DASHBOARD_NO_REDIRECT),
        },
        {
            id: "tab-pending-actions",
            label: (
                <MenuBadge count={count} hasOverdueActions={isOverdue}>
                    {t("Dashboard_PendingActions_Title")}
                </MenuBadge>
            ),
            onClick: () => navigate(dashboardPendingActivitiesUrl),
        },
    ];

    const tabsWithOverview = [
        {
            id: "tab-overview",
            label: t("Dashboard_Title_V2"),
            onClick: () => navigate(WebRoute.DASHBOARD_V2),
        },
        ...(controlHealthDashboard
            ? [
                  {
                      id: "tab-control-health",
                      label: t("ControlHealth_Title"),
                      onClick: () => navigate(WebRoute.CONTROL_HEALTH),
                  },
              ]
            : []),
        ...baseTabs,
    ];

    const getTabSpecs = () => {
        const dashboardOverviewAccess =
            userStatus.frontendOptions.dashboardOverviewAccess;
        switch (dashboardOverviewAccess) {
            case "Limited":
                return baseTabs;
            case "Full":
                return tabsWithOverview;
            case "ControlPrograms":
                return tabsWithOverview;
            default:
                return assertNever(dashboardOverviewAccess);
        }
    };
    const tabSpecs = getTabSpecs();

    return (
        <>
            <OrgApplicationBar
                title={
                    newMenu
                        ? t("Dashboard_PendingActions_Title")
                        : t("Dashboard_Title")
                }
                navigation={{
                    type: "Tabs",
                    currentTabId: "tab-pending-actions",
                    tabSpecs,
                }}
            >
                <DashboardPendingActivitiesList
                    rows={rows}
                    arrivedFromPendingTests={arrivedFromPendingTests}
                />
            </OrgApplicationBar>
        </>
    );
}
